import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Input, Select, Dropdown, Button } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { formatToSQLDate, parseSQLDate } from "../../../utils/dateUtils";
import CustomButton from "../ui/CustomButton";
import LocalizedDatePicker, { LocalizedRangePicker } from "../ui/LocalizedDatePicker";
import CommonSelectLite from "../CommonSelectLite";

export interface FilterField {
  key: string;
  label: string;
  type: "text" | "select" | "date" | "range";
  options?: { label: string; value: string | number }[]; // For select fields
  rangeKeys?: { start: string; end: string };
}

interface TableFilterProps {
  fields: FilterField[];
  filters: { [key: string]: string | number | null };
  onApply: (newFilters: { [key: string | number]: string | number | null }) => void;
  onReset: () => void;
}

const TableFilterDynamique: React.FC<TableFilterProps> = ({
  fields,
  filters,
  onApply,
  onReset,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [localFilters, setLocalFilters] = useState({ ...filters });

  useEffect(() => {
    setLocalFilters({ ...filters });
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    
    fields.forEach(field => {
      if (field.type === "range") {
        const { startKey, endKey } = {
          startKey: field.rangeKeys?.start ?? `${field.key}_start`,
          endKey: field.rangeKeys?.end ?? `${field.key}_end`,
        };
        if (filters[startKey] || filters[endKey]) {
          count++;
        }
      } else {
        const value = filters[field.key];
        if (value !== null && value !== undefined && value !== "") {
          count++;
        }
      }
    });
    
    return count;
  }, [filters, fields]);

  const handleFilterChange = (key: string, value: string | number | null) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Helpers for range fields
  const getRangeKeys = (field: FilterField) => ({
    startKey: field.rangeKeys?.start ?? `${field.key}_start`,
    endKey: field.rangeKeys?.end ?? `${field.key}_end`,
  });

  const readRangeValue = (field: FilterField) => {
    const { startKey, endKey } = getRangeKeys(field);
    const startRaw = (localFilters[startKey] as string) ?? null;
    const endRaw = (localFilters[endKey] as string) ?? null;
    return [parseSQLDate(startRaw), parseSQLDate(endRaw)] as const;
  };

  const writeRangeValue = (field: FilterField, start: any, end: any) => {
    const { startKey, endKey } = getRangeKeys(field);
    setLocalFilters((prev) => ({
      ...prev,
      [startKey]: formatToSQLDate(start || null),
      [endKey]: formatToSQLDate(end || null),
    }));
  };

  const handleReset = () => {
    setLocalFilters({ ...filters }); // reset to initial state, not empty
    onReset();
    setVisible(false);
  };

  const handleApply = () => {
    onApply(localFilters);
    setVisible(false);
  };

  const dropdownContent = (
    <div className="filter-dropdown bg-white shadow-sm p-3" style={{ width: 320 }}>
      <h4 className="mb-3">{t("filter")}</h4>

      {fields.map((field) => (
        <div key={field.key} className="mb-3">
          <label className="form-label">{t(field.label)}</label>

          {field.type === "text" && (
            <Input
              value={(localFilters[field.key] as string) || ""}
              onChange={(e) => handleFilterChange(field.key, e.target.value)}
              placeholder={t("search_placeholder", { label: t(field.label) })}
            />
          )}

          {field.type === "select" && field.options && (
            <CommonSelectLite
              value={(localFilters[field.key] as string | number | null) ?? undefined}
              onChange={(value) => handleFilterChange(field.key, value)}
              placeholder={t("select_placeholder", { label: t(field.label) })}
              options={field.options}
              optionValueKey="value"
              optionLabelKey="label"
            />
          )}

          {field.type === "date" && (
            <LocalizedDatePicker
              value={parseSQLDate(localFilters[field.key] as string)}
              onChange={(date) => handleFilterChange(field.key, formatToSQLDate(date))}
              placeholder={t("pick_placeholder", { label: t(field.label) })}
              style={{ width: "100%" }}
            />
          )}

          {field.type === "range" && (
            <LocalizedRangePicker
              value={readRangeValue(field) as any}
              onChange={(dates) => {
                const [start, end] = dates || [];
                writeRangeValue(field, start, end);
              }}
              placeholder={[t("start_date"), t("end_date")]}
              style={{ width: "100%" }}
            />
          )}
        </div>
      ))}

      <div className="d-flex justify-content-between mt-3">
        <Button className="btn-secondary" onClick={handleReset}>
          {t("reset")}
        </Button>
        <Button className="btn-primary" type="primary" onClick={handleApply}>
          {t("apply")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="card-header d-flex align-items-center justify-content-end">
      <Dropdown
        open={visible}
        trigger={["click"]}
        onOpenChange={setVisible}
        placement="bottomLeft"
        dropdownRender={() => dropdownContent}
      >
        <CustomButton 
          size="md" 
          className="btn-primary" 
          onClick={() => setVisible(!visible)}
        >
          <div className="d-flex align-items-center gap-2 position-relative">
            <FilterOutlined />
            {t("filter")}
            
            {activeFilterCount > 0 && (
              <span 
                className="badge rounded-pill bg-danger"
                style={{
                  minWidth: "18px",
                  height: "18px",
                  fontSize: "10px",
                  padding: "0 4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  border: "2px solid white"
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </div>
        </CustomButton>
      </Dropdown>
    </div>
  );
};

export default TableFilterDynamique;